import React, { useEffect } from 'react';
import { z } from 'zod';

import { useToaster } from '@crossnokaye/ux-common';

import icon from '@/assets/images/icon.png';
import { AcceptInviteForm, SetPasswordForm } from '@/components/forms';
import { setPasswordSchema } from '@/components/schemas';
import { InvitationAction, InvitationType } from '@/components/types';
import { AppConfigs } from '@/config';
import { sdk } from '@/singletons';

interface InvitePageProps {
  id: string;
  email: string;
  type: InvitationType;
  action: InvitationAction;
}

const InvalidInvitation = () => {
  return (
    <div className={'flex flex-col items-center h-screen mt-20'}>
      <div className={'bg-background rounded-lg p-5 max-w-[660px] w-full text-center'}>
        <h2 className={'text-primary text-xl mb-4'}>{'Invalid Invitation Link'}</h2>
        <p className={'text-white'}>
          {'Please contact your administrator to help resolve the issue.'}
        </p>
      </div>
    </div>
  );
};

export const InvitePage: React.FC<InvitePageProps> = ({ id, email, type, action }) => {
  const isValidType =
    Object.values(InvitationType).includes(type as InvitationType) &&
    Object.values(InvitationAction).includes(action as InvitationAction) &&
    z.string().email().safeParse(email).success &&
    id.toString() !== '';

  const { addErrorToast, addSuccessToast } = useToaster();

  // If the action is LOGIN, redirect to the login page
  useEffect(() => {
    if (action === InvitationAction.LOGIN) {
      window.location.href = AppConfigs.atlasEnvUrl.toString();
    }
  }, [action]);

  const handleAccept = React.useCallback(async (): Promise<void> => {
    await sdk
      .acceptInvitationUnauthorized(id.toString(), email)
      .then(async () => {
        addSuccessToast('Successfully set password');
        window.location.href = AppConfigs.atlasEnvUrl.toString();
      })
      .catch(async (error) => {
        const err = await error.json();
        const errorMessage = err.message || 'Failed to set password';
        addErrorToast('Failed to set password: ' + errorMessage);
        return err;
      });
  }, [id, email, addErrorToast, addSuccessToast]);

  const handleSubmitPassword = React.useCallback(
    async (values: z.infer<typeof setPasswordSchema>): Promise<void> => {
      await sdk
        .acceptInvitationUnauthorized(id.toString(), email, values.password)
        .then(async () => {
          addSuccessToast('Successfully set password');
          window.location.href = AppConfigs.atlasEnvUrl.toString();
        })
        .catch(async (error) => {
          const err = await error.json();
          const errorMessage = err.message || 'Failed to set password';
          addErrorToast('Failed to set password: ' + errorMessage);
          return err;
        });
    },
    [id, email, addErrorToast, addSuccessToast]
  );

  if (!isValidType) {
    return <InvalidInvitation />;
  }

  return (
    <div className={'flex flex-col items-center h-screen mt-20'}>
      <div
        className={
          'flex flex-col items-center justify-center bg-background rounded-lg p-5 max-w-[660px] w-full gap-y-4'
        }
      >
        {/* Header */}
        <div className={'flex flex-row items-center justify-between w-full'}>
          <div className={'flex flex-row items-center gap-2'}>
            <img alt={'Atlas Logo'} className={'w-8 h-8'} src={icon} />
            <div className={'uppercase text-white tracking-[0.0625rem]'}>{'Atlas'}</div>
            <div className={'uppercase text-[#b1ff00] text-sm tracking-[0.0625rem]'}>
              {'Platform'}
            </div>
          </div>
          <div className={'text-[0.625rem]'}>
            {'Powered by'}
            <span className={'font-bold'}>{'CrossnoKaye'}</span>
          </div>
        </div>

        {/* Welcome to Atlas */}
        <div
          className={`
          pt-6 w-full h-[200px]
          bg-[linear-gradient(to_bottom,rgba(17,17,17,0)_66.67%,rgba(17,17,17,1)_100%),url('@/assets/images/email-background.png')]
          bg-center bg-no-repeat bg-cover
        `}
        >
          <div
            className={`bg-gradient-to-r from-[#b1ff00] to-[#46d401] bg-clip-text text-transparent 
              text-[1.75rem] font-bold tracking-[0.3125rem] leading-[1.75rem] mb-5 text-center uppercase`}
          >
            {'Welcome To'}
          </div>
          <div
            className={
              'text-center uppercase text-8xl text-white tracking-[0.8125rem] leading-[5rem] font-bold'
            }
          >
            {'Atlas'}
          </div>
        </div>

        {action === InvitationAction.SIGNUP ? (
          <React.Fragment>
            {type.toLowerCase() === InvitationType.UNP ? (
              // The SIGNUP action and the UNP type indicated that the User will need to setup a Username/password combination.
              <React.Fragment>
                <div className={'text-white leading-6 mx-10'}>
                  <div>
                    <span>{`Please set the password for `}</span>
                    <span className={'font-bold underline'}>{email}</span>
                    <span>{` to continue.`}</span>
                  </div>
                </div>
                <div className={'bg-background rounded-lg p-5'}>
                  <SetPasswordForm onSubmit={handleSubmitPassword} />
                </div>
              </React.Fragment>
            ) : type.toLowerCase() === InvitationType.IDP ? (
              // The SIGNUP action and the IDP type indicates that the User is using some kind of IDP and only needs to accept the invitation.
              <React.Fragment>
                <div className={'text-white leading-6 mx-10'}>
                  <div>
                    <span>{`Click to continue as `}</span>
                    <span className={'font-bold underline'}>{email}</span>
                  </div>
                </div>

                {/* Form */}
                <div className={'bg-background rounded-lg p-5'}>
                  <AcceptInviteForm onSubmit={handleAccept} />
                </div>
              </React.Fragment>
            ) : (
              <InvalidInvitation />
            )}
          </React.Fragment>
        ) : (
          <InvalidInvitation />
        )}
      </div>
    </div>
  );
};
